import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import seoData from '../seo/services.json';
import './Services.css';

const services = [
  {
    id: 'tax',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    title: 'Tax Management Services',
    short: 'Strategic tax planning and preparation to minimize liabilities and maximize cash flow.',
    detail: 'We guide our clients through a full range of tax planning and preparation decisions with strategies that minimize tax liabilities, maximize cash flow, and keep you on track to your financial goals. Our expertise, experience, analysis, and thorough research allow us to optimize financial opportunities found in existing as well as recently altered tax laws. We are knowledgeable and up to date on tax laws and can make sense of your receipts, bills, and notices.',
  },
  {
    id: 'accounting',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
    title: 'Accounting Services',
    short: 'Accurate record-keeping and financial reporting to drive profitability.',
    detail: 'From start-ups to established enterprises, businesses rely on accurate and insightful financial information to maintain profitability. Our accounting services include general ledger and financial statement preparation, bookkeeping (monthly, quarterly, or annual), accounting system setup and support, payroll processing, cash flow budgeting and forecasting, personal financial statements, employee benefit and profit-sharing plans, corporate tax planning, and litigation support.',
  },
  {
    id: 'assurance',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    title: 'Assurance & Advisory',
    short: 'Building long-term value through objective analysis and strategic guidance.',
    detail: 'We focus on building close client relationships that add long-term value. This includes assuring the solidity of financial records, evaluating procedures, and producing strategies for business challenges. Services include audit assistance, entity selection and restructuring, accounting software support, business valuations, financial projections, debt advising, mergers and acquisitions, transaction structuring, and business succession planning.',
  },
  {
    id: 'estate',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    title: 'Estate & Trust Planning',
    short: 'Ensure financial security for loved ones and smooth business succession.',
    detail: 'Effective estate and trust planning can ensure financial security for loved ones and maintain smooth succession of ownership for businesses. We help navigate complex and shifting tax laws to facilitate the transfer of assets and minimize tax liability for your beneficiaries. A well-thought-out plan ensures your wishes are followed without complications.',
  },
  {
    id: 'retirement',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    title: 'Financial & Retirement Planning',
    short: 'Achieve financial independence through a comfortable retirement.',
    detail: 'Our goal is to help you reach your financial goals and maintain financial independence. We guide you through asset allocation, investment performance monitoring, college funding, retirement plans, insurance, tax planning, estate and business succession. We ensure your portfolio accounts for tax implications for advantageous after-tax returns.',
  },
  {
    id: 'litigation',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    title: 'Litigation Support & Forensic Accounting',
    short: 'Expert analysis for commercial litigation and financial investigations.',
    detail: 'Our litigation support and forensic accounting services cover commercial litigation, financial investigations, business valuations, dissolutions, and bankruptcies. Services include quantification of economic loss, analysis of financial records, and fraud and theft investigation. We make sense of complex records and transactions to get to your bottom line.',
  },
  {
    id: 'bookkeeping',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
    title: 'Bookkeeping / Write-up',
    short: 'Organized, accurate record-keeping so you can focus on your business.',
    detail: 'Accurate record-keeping is essential to a successful business yet can be complicated and time consuming. We help with the organization and day-to-day tasks of bookkeeping so that you can focus on your core business.',
  },
  {
    id: 'quickbooks',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
    title: 'QuickBooks Help & Assistance',
    short: 'Training, installation, setup, support, and review for QuickBooks.',
    detail: 'QuickBooks can provide useful financial information, but only when installed, set up, and used properly. We offer personal training, installation, setup assistance, ongoing support, and thorough reviews of your transactions, accounts, and reports to ensure timely, relevant, and reliable financial information.',
  },
  {
    id: 'payroll',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    title: 'Payroll Services',
    short: 'Computerized payroll systems with timely payment and tax return preparation.',
    detail: 'Our payroll services help reduce the time spent on administration through developing and implementing a computerized payroll system that facilitates processing, timely payment, and preparation of tax returns.',
  },
  {
    id: 'cashflow',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    title: 'Cash Flow & Budgeting Analysis',
    short: 'Optimize cash flow levels to improve liquidity and profitability.',
    detail: 'Good cash management can improve liquidity, reduce costs, and increase profitability. We help maintain optimal cash flow levels by tracking sources and uses, forecasting, and budgeting accordingly. We analyze spending, rebalance budgets, and create optimal cash flow plans for your business success.',
  },
  {
    id: 'valuation',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    title: 'Business Valuation',
    short: 'In-depth analysis of factors driving your company\'s value.',
    detail: 'We provide in-depth analysis to identify and analyze factors that drive business value. Uses include estate planning, buying and selling businesses, divorce matters, family limited partnerships, and ESOPs. Our expert help evaluates entities and guides you towards confident business decisions.',
  },
  {
    id: 'international',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    title: 'International Taxation',
    short: 'Navigate cross-border taxation for corporations and individuals.',
    detail: 'Whether you\'re a corporation with overseas operations or need to work out taxation of U.S. residents working abroad or foreign citizens working in the U.S., we help plot a course through cross-border taxation issues and special rules for businesses with a foreign presence.',
  },
];

export default function Services() {
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && services.some((s) => s.id === hash)) {
      setExpanded(hash);
      setTimeout(() => {
        const el = document.getElementById(`service-${hash}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [location.hash]);

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  return (
    <>
      <SEOHead seo={seoData} />

      {/* ===== PAGE HEADER ===== */}
      <section className="page-header">
        <div className="page-header__bg" />
        <div className="container">
          <div className="page-header__content">
            <span className="section-label" style={{ color: 'var(--gold-light)' }}>Our Services</span>
            <h1 className="page-header__title">Comprehensive Financial Solutions</h1>
            <p className="page-header__subtitle">
              A full range of professional services tailored to meet your individual and business needs.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SERVICES LIST ===== */}
      <section className="section services-page">
        <div className="container">
          <div className="services-page__grid">
            {services.map((s, i) => (
              <div
                key={s.id}
                id={`service-${s.id}`}
                className={`svc-card ${expanded === s.id ? 'svc-card--expanded' : ''}`}
                style={{ animationDelay: `${0.05 + i * 0.04}s` }}
              >
                <div className="svc-card__header" onClick={() => toggleExpand(s.id)}>
                  <div className="svc-card__icon">{s.icon}</div>
                  <div className="svc-card__info">
                    <h3 className="svc-card__title">{s.title}</h3>
                    <p className="svc-card__short">{s.short}</p>
                  </div>
                  <button className="svc-card__toggle" aria-label="Toggle details">
                    <svg
                      width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transform: expanded === s.id ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>
                <div className={`svc-card__detail ${expanded === s.id ? 'svc-card__detail--open' : ''}`}>
                  <div className="svc-card__detail-inner">
                    <p>{s.detail}</p>
                    <Link to="/contact" className="svc-card__cta">
                      Request Information
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="services-cta">
        <div className="services-cta__bg" />
        <div className="container">
          <div className="services-cta__content text-center">
            <h2 style={{ color: 'var(--white)', marginBottom: '16px' }}>
              Don't See What You Need?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
              Contact us to discuss your specific financial needs. We offer customized solutions for every situation.
            </p>
            <Link to="/contact" className="btn btn-gold btn--lg">
              Schedule a Consultation
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
